import React from 'react';
import { Trans } from '@lingui/react/macro';
import theme from './ChangeRequestParticipantOfferLines.module.scss';
import { ChangeRequestParticipantLineInfo } from '@/types';
import { FeeSetConnectionInfoDisplay, PriceDisplay } from '@/seller/components/fees';
import { sumBy } from 'lodash-es';
import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import Tags from '@/components/Tags';

interface ChangeRequestParticipantOfferLinesProps {
    offerLines: ChangeRequestParticipantLineInfo[] | null;
    priceIsRedacted?: boolean;
}

const ChangeRequestParticipantOfferLines: React.FC<ChangeRequestParticipantOfferLinesProps> = ({
    offerLines,
    priceIsRedacted,
}) => {
    if (!offerLines || offerLines.length === 0) {
        return null;
    }

    const renderTotalPrice = () => {
        return (
            <PriceDisplay
                value={{
                    basePrice: sumBy(offerLines, (x) => x.priceCalculation?.basePrice ?? 0),
                    finalPrice: sumBy(offerLines, (x) => x.priceCalculation?.finalPrice ?? 0),
                }}
                redacted={priceIsRedacted}
            />
        );
    };

    return (
        <div className={theme.priceBreakdown}>
            <h6>
                <Trans>Price breakdown</Trans>
            </h6>
            <table className="table style-3">
                <thead>
                    <tr>
                        <th>
                            <Trans>Description</Trans>
                        </th>
                        <th className="nowrap">
                            <Trans>Fees</Trans>
                        </th>
                        <th className="nowrap">
                            <Trans>Category</Trans>
                        </th>
                        <th className="nowrap">
                            <Trans>Tags</Trans>
                        </th>
                        <th className="nowrap text-right">
                            <Trans>Amount</Trans>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {offerLines.map((line) => (
                        <tr key={line.id}>
                            <td>{line.description}</td>
                            {line.feeSetInfo && (
                                <td className="nowrap">
                                    <FeeSetConnectionInfoDisplay value={line.feeSetInfo} />
                                </td>
                            )}
                            <td>
                                <ItemCategoryDisplay value={line.itemCategoryId} />
                            </td>
                            <td>
                                <Tags size="small" tags={line.tags} limit={2} />
                            </td>
                            <td className="nowrap text-right">
                                <PriceDisplay
                                    value={{
                                        basePrice: line.priceCalculation?.basePrice ?? 0,
                                        finalPrice: line.priceCalculation?.finalPrice ?? 0,
                                    }}
                                    redacted={priceIsRedacted}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th colSpan={4}>
                            <Trans>Subtotal</Trans>
                        </th>
                        <th className="text-right">{renderTotalPrice()}</th>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default ChangeRequestParticipantOfferLines;
